import "@/App.css"
import { useAuthStore } from "@/entities/auth/auth.model"
import {
  clearPendingAssistantRequests,
  resumePendingAssistantStops,
} from "@/entities/assistant/assistant.pending-stop"
import { clearReviewCompletions } from "@/entities/notification/completion-alerts"
import { clearCompletionInbox } from "@/entities/notification/completion-inbox"
import { clearAssistantCompletionNotifications } from "@/entities/assistant/assistant.completion-notifications"
import { assistantKeys } from "@/entities/assistant/assistant.queries"
import { queryClient } from "@/shared/lib/react-query/query-client"
import * as Sentry from "@sentry/react"
import { routeTree } from "@/routeTree.gen"
import i18n from "@/shared/lib/react-i18next"
import { getAccessToken, getRefreshToken } from "@/shared/lib/token/token"
import NotFoundPage from "@/widgets/auth/not-found"
import RouteErrorPage from "@/widgets/overall/route-error-page"
import { useThemeInit } from "@/shared/lib/theme/theme"
import { EnvironmentFrame } from "@/shared/ui/environment/environment-indicator"
import { createRouter, RouterProvider } from "@tanstack/react-router"
import { useEffect } from "react"
import { Toaster } from "react-hot-toast"
import { I18nextProvider } from "react-i18next"
import { QueryClientProvider } from "./query-client-provider"

// Set up a Router instance
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  context: { auth: undefined! },
  defaultNotFoundComponent: () => <NotFoundPage />,
  defaultErrorComponent: RouteErrorPage,
})

// Register things for typesafety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

const Providers = () => {
  const auth = useAuthStore()

  useThemeInit()

  useEffect(
    () =>
      useAuthStore.subscribe((state, previous) => {
        if (
          previous.isAuthenticated &&
          (!state.isAuthenticated || previous.user.id !== state.user.id)
        ) {
          clearPendingAssistantRequests()
          clearAssistantCompletionNotifications()
          clearReviewCompletions()
          clearCompletionInbox()
        }
      }),
    []
  )

  useEffect(() => {
    if (auth.isAuthenticated) {
      resumePendingAssistantStops(() => {
        void queryClient.invalidateQueries({
          queryKey: assistantKeys.conversations(),
        })
      })
    }
  }, [auth.isAuthenticated])

  useEffect(() => {
    const accessToken = getAccessToken()
    const refreshToken = getRefreshToken()
    if (!accessToken || !refreshToken) return

    // `setUser` decodes the access token and throws on a malformed one. That
    // throw is deliberate and the hand-off routes depend on it, but here it is
    // uncaught and this effect runs on every boot -- so a stored token that
    // cannot be decoded takes the whole app down, on every load, with no way out
    // but clearing site data by hand. `/login/success` and `/marketplace/landing`
    // both note this bootstrap as the thing their ordering protects; they cannot
    // protect a token that was already persisted, by an older build or a partial
    // write.
    //
    // An undecodable token is an unusable session, so end it: `logout` clears the
    // tokens, the store and the caches, and the next boot starts clean instead of
    // repeating the crash. Reported rather than swallowed -- this path is only
    // reached by something already wrong.
    try {
      auth.setUser({ accessToken, refreshToken, email: "" })
    } catch (error) {
      Sentry.captureException(error, {
        tags: { boundary: "auth-bootstrap" },
      })
      auth.logout()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.setUser])

  return (
    <QueryClientProvider>
      <RouterProvider router={router} context={{ auth }} />
      <I18nextProvider i18n={i18n} />
      <EnvironmentFrame />
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            maxWidth: 1440,
            background: "hsl(var(--popover))",
            color: "hsl(var(--popover-foreground))",
            // 다크에서 토스트 면이 페이지와 가까워 테두리 없이는 경계가 사라진다.
            border: "1px solid hsl(var(--border))",
          },
        }}
      />
    </QueryClientProvider>
  )
}

export default Providers
